import React from 'react';
import {
  Users,
  Loader2,
  AlertCircle,
  UserCheck,
  Shield,
  Check,
  X,
  FileText,
  Briefcase
} from 'lucide-react';
import { usePermissions } from '../../../../contexts/PermissionContext';

const TeamPermissionsTab = () => {
  const { permissions, userRole, loading: permLoading } = usePermissions();

  // Group permissions by category
  const groupedPermissions = Object.entries(permissions).reduce((acc, [code, perm]) => {
    const category = perm.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push({ code, ...perm });
    return acc;
  }, {});

  const TOP_THREE_ROLES = ['primary_owner', 'owner', 'executive'];
  const isTopThree = TOP_THREE_ROLES.includes(userRole);

  const getCategoryLabel = (category) => {
    const labels = {
      workforce: 'Workforce',
      jobs: 'Jobs',
      inspections: 'Inspections',
      reports: 'Reports',
      dashboard: 'Dashboard',
      settings: 'Settings',
      other: 'Other'
    };
    return labels[category] || category;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      workforce: Users,
      jobs: Briefcase,
      inspections: FileText,
      reports: FileText,
      dashboard: Shield,
      settings: Shield,
      other: Shield
    };
    return icons[category] || Shield;
  };

  if (permLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Permissions</h2>
        <p className="text-text-secondary text-sm mt-1">
          View your role and permissions in this company
        </p>
      </div>

      {/* My Role */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Shield size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">My Role</h3>
        </div>

        <div className="p-4 bg-background border border-border rounded-lg inline-block">
          <span className="px-3 py-1.5 bg-gradient-to-r from-orange-500/20 to-orange-600/20 text-orange-400 rounded-lg text-sm font-medium capitalize">
            {userRole?.replace(/_/g, ' ') || 'Member'}
          </span>
        </div>
        <p className="text-text-secondary text-xs mt-2">
          Your permissions are based on this role, with any custom overrides applied.
        </p>
      </div>

      {/* My Permissions */}
      <div>
        <div className="flex items-center space-x-2 mb-4">
          <UserCheck size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">My Permissions</h3>
        </div>

        {Object.keys(groupedPermissions).length === 0 ? (
          <p className="text-text-secondary text-sm">No permissions data available.</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedPermissions).map(([category, perms]) => {
              const CategoryIcon = getCategoryIcon(category);
              return (
                <div key={category}>
                  <div className="bg-background border border-border rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-surface border-b border-border flex items-center space-x-2">
                      <CategoryIcon size={16} className="text-text-secondary" />
                      <span className="text-text-primary text-sm font-medium">{getCategoryLabel(category)}</span>
                    </div>
                    <div className="divide-y divide-border/50">
                      {perms.map(perm => (
                        <div
                          key={perm.code}
                          className="px-4 py-3 flex items-center justify-between"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-text-primary text-sm">{perm.name || perm.code}</p>
                            {perm.description && (
                              <p className="text-text-secondary text-xs mt-0.5 truncate">{perm.description}</p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            {perm.source === 'override' && (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                                Override
                              </span>
                            )}
                            {perm.granted ? (
                              <div className="flex items-center text-green-500">
                                <Check size={16} />
                              </div>
                            ) : (
                              <div className="flex items-center text-text-secondary">
                                <X size={16} />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Top-three self-approval note — shown below the inspections category */}
                  {category === 'inspections' && isTopThree && (
                    <div className="mt-2 flex items-start gap-2 px-4 py-3 bg-orange-500/5 border border-orange-500/20 rounded-lg text-xs text-text-secondary">
                      <Shield size={13} className="text-orange-400 mt-0.5 flex-shrink-0" />
                      <p>
                        As a <span className="text-text-primary font-medium capitalize">{userRole?.replace(/_/g, ' ')}</span>, you may approve inspection reports that you have personally conducted. This is a role-level privilege and cannot be modified.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="mt-4 flex items-center space-x-6 text-xs text-text-secondary">
          <div className="flex items-center space-x-1">
            <Check size={14} className="text-green-500" />
            <span>Granted</span>
          </div>
          <div className="flex items-center space-x-1">
            <X size={14} className="text-text-secondary" />
            <span>Not granted</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">Override</span>
            <span>Custom permission</span>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-background border border-border rounded-lg">
          <p className="text-text-secondary text-sm">
            <strong className="text-text-primary">Need different permissions?</strong>
          </p>
          <p className="text-text-secondary text-xs mt-1">
            Contact your manager or a company administrator to request permission changes.
            Permission overrides can be granted based on your specific role and responsibilities.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TeamPermissionsTab;
